import { mergePlanRenderRequests } from './plan_render_request.js';

const APP_PERF_STEP_WARN_MS = 16;
const APP_PERF_TOTAL_WARN_MS = 50;

let appPerfMeasureCounter = 0;
let appPerfProfileEnabled = false;
let bomPerfProfileEnabled = false;
let appRefreshFrame = 0;
let appRefreshFit = false;
let appRefreshGeneration = 0;
let planRenderFrame = 0;
let planIdleRenderFrame = 0;
let pendingPlanRenderRequest = null;
let planRenderGeneration = 0;
let planNavigationLodTimer = 0;
let planNavigationLodUntil = 0;

function appPerfEnabled() {
  return appPerfProfileEnabled === true;
}

function bomPerfEnabled() {
  if (appPerfEnabled()) return true;
  return bomPerfProfileEnabled === true;
}

function setAppPerfEnabled(value) {
  appPerfProfileEnabled = value === true;
  return appPerfProfileEnabled;
}

function setBomPerfEnabled(value) {
  bomPerfProfileEnabled = value === true;
  return bomPerfProfileEnabled;
}

function appPerfTimer(label) {
  if (!appPerfEnabled() || typeof performance === 'undefined') return null;
  const now = performance.now();
  const id = ++appPerfMeasureCounter;
  const startMark = `ks:${label}:${id}:start`;
  if (typeof performance.mark === 'function') performance.mark(startMark);
  return { label, start: now, last: now, startMark, endMark: `ks:${label}:${id}:end` };
}

function appPerfStep(timer, step, threshold = APP_PERF_STEP_WARN_MS) {
  if (!timer || typeof performance === 'undefined') return;
  const now = performance.now();
  const elapsed = now - timer.last;
  timer.last = now;
  if (elapsed > threshold && typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[KS perf] ${timer.label}:${step} ${elapsed.toFixed(1)}ms`);
  }
}

function appPerfEnd(timer, threshold = APP_PERF_TOTAL_WARN_MS) {
  if (!timer || typeof performance === 'undefined') return;
  const elapsed = performance.now() - timer.start;
  if (typeof performance.mark === 'function' && typeof performance.measure === 'function') {
    performance.mark(timer.endMark);
    performance.measure(`ks:${timer.label}`, timer.startMark, timer.endMark);
    if (typeof performance.clearMarks === 'function') {
      performance.clearMarks(timer.startMark);
      performance.clearMarks(timer.endMark);
    }
  }
  if (elapsed > threshold && typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[KS perf] ${timer.label}:total ${elapsed.toFixed(1)}ms`);
  }
}

function installAppPerfHelpers() {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return;
  if (typeof window.KS_PERF_ON !== 'function') {
    window.KS_PERF_ON = (scope = 'app') => {
      if (scope === 'bom') {
        setBomPerfEnabled(true);
        return true;
      }
      setAppPerfEnabled(true);
      return true;
    };
  }
  if (typeof window.KS_PERF_OFF !== 'function') {
    window.KS_PERF_OFF = (scope = 'all') => {
      if (scope === 'bom') {
        setBomPerfEnabled(false);
        return false;
      }
      setAppPerfEnabled(false);
      if (scope === 'all') setBomPerfEnabled(false);
      return false;
    };
  }
  if (typeof window.KS_PERF_CLEAR !== 'function') {
    window.KS_PERF_CLEAR = () => {
      if (typeof performance.clearMeasures === 'function') performance.clearMeasures();
      if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info('[KS perf] measures cleared');
      }
    };
  }
  if (typeof window.KS_PERF_SUMMARY !== 'function') {
    window.KS_PERF_SUMMARY = (limit = 30, sortBy = 'max') => {
      const entries = typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('measure').filter(entry => entry.name.startsWith('ks:'))
        : [];
      const groups = new Map();
      for (const entry of entries) {
        const current = groups.get(entry.name) || {
          name: entry.name,
          count: 0,
          total: 0,
          max: 0,
          last: 0
        };
        const duration = entry.duration || 0;
        current.count += 1;
        current.total += duration;
        current.max = Math.max(current.max, duration);
        current.last = duration;
        groups.set(entry.name, current);
      }
      const rows = [...groups.values()].map(row => ({
        name: row.name,
        count: row.count,
        max: Math.round(row.max * 10) / 10,
        avg: Math.round((row.total / Math.max(1, row.count)) * 10) / 10,
        total: Math.round(row.total * 10) / 10,
        last: Math.round(row.last * 10) / 10
      }));
      const sortKey = ['count', 'avg', 'total', 'last'].includes(sortBy) ? sortBy : 'max';
      rows.sort((a, b) => b[sortKey] - a[sortKey]);
      const result = rows.slice(0, Math.max(1, Number(limit) || 30));
      if (typeof console !== 'undefined' && typeof console.table === 'function') console.table(result);
      return result;
    };
  }
}

function requestFrame(callback) {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback);
  return setTimeout(callback, 16);
}

function cancelFrame(frameId) {
  if (!frameId) return;
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameId);
  else clearTimeout(frameId);
}

function requestIdleFrame(callback, timeoutMs = 500) {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(callback, { timeout: timeoutMs });
  }
  return requestFrame(callback);
}

function cancelIdleFrame(id) {
  if (!id) return;
  if (typeof cancelIdleCallback === 'function') cancelIdleCallback(id);
  else cancelFrame(id);
}

function cancelAppRefresh() {
  appRefreshGeneration += 1;
  if (!appRefreshFrame) return false;
  cancelFrame(appRefreshFrame);
  appRefreshFrame = 0;
  appRefreshFit = false;
  return true;
}

function consumeAppRefreshFit(fit = false) {
  const nextFit = Boolean(fit) || appRefreshFit;
  cancelAppRefresh();
  return nextFit;
}

function cancelPlanRender() {
  planRenderGeneration += 1;
  pendingPlanRenderRequest = null;
  let cancelled = false;
  if (planIdleRenderFrame) {
    cancelIdleFrame(planIdleRenderFrame);
    planIdleRenderFrame = 0;
    cancelled = true;
  }
  if (!planRenderFrame) return cancelled;
  cancelFrame(planRenderFrame);
  planRenderFrame = 0;
  return true;
}

function requestPlanRender(callback, request) {
  if (appRefreshFrame) return false;
  pendingPlanRenderRequest = mergePlanRenderRequests(pendingPlanRenderRequest, request);
  if (planIdleRenderFrame) {
    cancelIdleFrame(planIdleRenderFrame);
    planIdleRenderFrame = 0;
    planRenderGeneration += 1;
  }
  if (planRenderFrame) return false;
  const generation = ++planRenderGeneration;
  planRenderFrame = requestFrame(() => {
    if (generation !== planRenderGeneration) return;
    const mergedRequest = pendingPlanRenderRequest;
    pendingPlanRenderRequest = null;
    planRenderFrame = 0;
    if (typeof callback === 'function') callback(mergedRequest);
  });
  return true;
}

function requestIdlePlanRender(callback, request) {
  if (appRefreshFrame) return false;
  pendingPlanRenderRequest = mergePlanRenderRequests(pendingPlanRenderRequest, request);
  if (planRenderFrame || planIdleRenderFrame) return false;
  const generation = ++planRenderGeneration;
  planIdleRenderFrame = requestIdleFrame(() => {
    if (generation !== planRenderGeneration) return;
    const mergedRequest = pendingPlanRenderRequest;
    pendingPlanRenderRequest = null;
    planIdleRenderFrame = 0;
    if (typeof callback === 'function') callback(mergedRequest);
  }, 500);
  return true;
}

function startPlanNavigationLod(onIdle, reason = 'nav') {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  planNavigationLodUntil = now + 220;
  if (planNavigationLodTimer) clearTimeout(planNavigationLodTimer);
  planNavigationLodTimer = setTimeout(() => {
    planNavigationLodTimer = 0;
    planNavigationLodUntil = 0;
    if (typeof onIdle === 'function') onIdle(reason);
  }, 240);
  return true;
}

function isPlanNavigationLodActive() {
  const until = Number(planNavigationLodUntil || 0);
  if (!Number.isFinite(until) || until <= 0) return false;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return now <= until;
}

function requestAppRefresh(callback, fit = false) {
  appRefreshFit ||= Boolean(fit);
  cancelPlanRender();
  if (appRefreshFrame) return false;
  const generation = ++appRefreshGeneration;
  appRefreshFrame = requestFrame(() => {
    if (generation !== appRefreshGeneration) return;
    const nextFit = appRefreshFit;
    appRefreshFrame = 0;
    appRefreshFit = false;
    if (typeof callback === 'function') callback(nextFit);
  });
  return true;
}

function appRequestAnimationFrame(callback) {
  return requestFrame(callback);
}

function appCancelAnimationFrame(frameId) {
  cancelFrame(frameId);
}

installAppPerfHelpers();

export {
  APP_PERF_STEP_WARN_MS,
  APP_PERF_TOTAL_WARN_MS,
  appPerfMeasureCounter,
  appPerfEnabled,
  bomPerfEnabled,
  setAppPerfEnabled,
  setBomPerfEnabled,
  appPerfTimer,
  appPerfStep,
  appPerfEnd,
  installAppPerfHelpers,
  requestFrame,
  cancelFrame,
  requestIdleFrame,
  cancelIdleFrame,
  cancelAppRefresh,
  consumeAppRefreshFit,
  cancelPlanRender,
  requestPlanRender,
  requestIdlePlanRender,
  startPlanNavigationLod,
  requestAppRefresh,
  appRequestAnimationFrame,
  appCancelAnimationFrame,
  isPlanNavigationLodActive
};
